import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface PatientEmailHistoryItem {
  id: string;
  clinic_id: string;
  invoice_id: string;
  sent_to: string;
  sent_by: string | null;
  postmark_message_id: string | null;
  status: 'sent' | 'failed';
  error_message: string | null;
  pdf_storage_path: string;
  subject: string;
  created_at: string;
  invoice_number: string | null;
}

export const patientEmailHistoryQueryKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['email-history', 'patient', clinicId, patientId] as const;

export function usePatientEmailHistory(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: patientEmailHistoryQueryKey(clinicId, patientId),
    enabled: Boolean(clinicId) && Boolean(patientId),
    queryFn: async (): Promise<PatientEmailHistoryItem[]> => {
      if (!clinicId || !patientId) return [];

      // First, get all invoice IDs for this patient
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select('id')
        .eq('patient_id', patientId)
        .eq('clinic_id', clinicId);

      if (invoicesError) throw invoicesError;

      const invoiceIds = (invoices ?? []).map((inv) => inv.id);

      if (invoiceIds.length === 0) {
        return [];
      }

      // Query invoice_emails for these invoice IDs and join with invoices to get invoice_number
      const { data, error } = await supabase
        .from('invoice_emails')
        .select(
          `id, clinic_id, invoice_id, sent_to, sent_by, postmark_message_id,
           status, error_message, pdf_storage_path, subject, created_at,
           invoice:invoices(invoice_number)`,
        )
        .eq('clinic_id', clinicId)
        .in('invoice_id', invoiceIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data ?? []).map((row): PatientEmailHistoryItem => {
        const invoice = (
          row as unknown as {
            invoice: { invoice_number: string } | null;
          }
        ).invoice;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          invoice_id: row.invoice_id,
          sent_to: row.sent_to,
          sent_by: row.sent_by,
          postmark_message_id: row.postmark_message_id,
          status: row.status as 'sent' | 'failed',
          error_message: row.error_message,
          pdf_storage_path: row.pdf_storage_path,
          subject: row.subject,
          created_at: row.created_at,
          invoice_number: invoice?.invoice_number ?? null,
        };
      });
    },
    staleTime: 15_000,
  });
}
